'use client'

import { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import MUIPaper from '@mui/material/Paper'
import MUITypography from '@mui/material/Typography'
import MUIStack from '@mui/material/Stack'
import { PopupWithBackdrop } from '@repo/ui/PopupWithBackdrop'
import { useMenuState, MenuItem } from '@szhsin/react-menu'
import { TickIcon } from '@repo/ui/TickIcon'
import { CaretDownIcon } from '@repo/ui/CaretDownIcon'
import { SettingsButtonAndPopup } from '@repo/ui/SettingsButtonAndPopup'
import { useTheme } from '@mui/material/styles'

export const DashboardPanel = ({
  title,
  options,
  onOptionSelected,
  selectedOption,
  children,
  sx,
  variant = 'default',
  settingsPopup = null,
  inlineConfig = null
}) => {
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const ref = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOptionValue, setSelectedOptionValue] = useState(selectedOption)
  const theme = useTheme()

  useEffect(() => {
    setSelectedOptionValue(selectedOption)
  }, [setSelectedOptionValue, selectedOption])

  useEffect(() => {
    setIsOpen(menuState?.state?.includes('open'))
  }, [setIsOpen, menuState])

  const handleSelection = ({ label, value }) => {
    onOptionSelected({ label, value })
    toggleMenu(false)
  }

  return (
    <MUIPaper
      data-testid="DashboardPanel"
      sx={{
        width: '100%',
        height: '100%',
        padding: '0',
        background: 'white',
        borderRadius: 1,
        boxShadow: 'none',
        border: '1px solid #D8DCE5',
        ...sx
      }}
    >
      <MUIStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          padding: '0 16px',
          mt: 4,
          height: variant === 'small' ? '40px' : '48px',
          position: 'relative',
          zIndex: 8
        }}
      >
        <MUIStack
          direction="row"
          alignItems="center"
          sx={{ cursor: options?.length ? 'pointer' : 'default' }}
          onClick={() => toggleMenu(true)}
          ref={ref}
        >
          <MUITypography variant="h5" sx={{ padding: '0', mt: 0, mb: 0, mr: 1, pointerEvents: 'none' }}>
            {title}
          </MUITypography>
          {options?.length ? (
            <CaretDownIcon
              sx={{
                color: theme?.palette?.support?.navy || 'black',
                width: '16px',
                height: '16px',
                transform: `rotate(${isOpen ? -180 : 0}deg)`,
                transition: 'all 250ms'
              }}
            />
          ) : null}
        </MUIStack>

        {settingsPopup ? <SettingsButtonAndPopup settingsPopup={settingsPopup} /> : null}
        {inlineConfig && !settingsPopup ? inlineConfig : null}

        {options ? (
          <PopupWithBackdrop
            anchorRef={ref}
            menuState={menuState}
            toggleMenu={toggleMenu}
            className="dashboardPanelMenu"
          >
            {options.map(({ label, value, disabled = false }) => (
              <MenuItem
                key={label}
                onClick={() => handleSelection({ label, value })}
                className={selectedOptionValue === value ? 'selected' : ''}
                disabled={disabled}
              >
                <MUIStack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  {label}
                  {selectedOptionValue === value ? <TickIcon /> : null}
                </MUIStack>
              </MenuItem>
            ))}
          </PopupWithBackdrop>
        ) : null}
      </MUIStack>

      {children}
    </MUIPaper>
  )
}

DashboardPanel.propTypes = {
  title: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.any })),
  selectedOption: PropTypes.any,
  onOptionSelected: PropTypes.func,
  children: PropTypes.node,
  sx: PropTypes.object,
  variant: PropTypes.oneOf(['default', 'small']),
  settingsPopup: PropTypes.any,
  inlineConfig: PropTypes.node
}
